import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Env from '../../shared/config/envConfig'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
} from '@mui/material'
import { makeStyles, styled } from '@mui/styles'
import CardComponent from './CardComponent'

import ReportIcon from '@mui/icons-material/Report'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import UpdateIcon from '@mui/icons-material/Update'

import { useTranslation } from 'react-i18next'
import formatDate from '@3m5/crude-frontend/dist/shared/util/formatDate'
import { addCsrfHeaders } from '../../shared/config/csrf'
import { useSelector } from 'react-redux'
import { getCrudeAppName } from '../../store/settings/settings.selectors'
import { Format } from '@3m5/crude-frontend/dist/defaults/getDefaultFormat'

const useStyles = makeStyles(() => ({
  root: {
    margin: '40px 0',
  },
}))

interface DashboardData {
  machinesWithoutLicense?: MachineData[]
  expiredLicenses?: LicenseData[]
  soonExpiredLicenses?: LicenseData[]
  errorLogs?: ErrorLogData[]
}

interface MachineData {
  machineId: string
  machineName: string
  machineKey: string
}

interface LicenseData {
  id: string
  name: string
  expires: string
}

interface ErrorLogData {
  error: string
  licenseKey: string
  machineName: string
  machineKey: string
  createdAt: string
}

const Dashboard: React.FC = () => {
  const classes = useStyles()
  const theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()
  const history = useHistory()
  const appName = useSelector(getCrudeAppName)

  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Content-Type', 'application/json')
  addCsrfHeaders(requestHeaders)

  const StyledTableRow = styled(TableRow)(() => ({
    '&:last-child td, &:last-child th': {
      border: 0,
    },
    '& td, th': {
      border: 0,
    },
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  }))

  const StyledTableHead = styled(TableRow)(() => ({
    '&:last-child td, &:last-child th': {
      border: 0,
    },
    '& td, th': {
      border: 0,
    },
  }))

  const [showDeleteRequest, setShowDeleteRequest] = useState<boolean>(false)
  const [deleteMachine, setDeleteMachine] = useState<string | undefined>()

  // Zustandsvariablen für den Verlängerungsdialog
  const [showExtendLicenseDialog, setShowExtendLicenseDialog] = useState<boolean>(false)
  const [extendLicenseId, setExtendLicenseId] = useState<string | undefined>()
  const [newExpiryDate, setNewExpiryDate] = useState<Date | null>(null)

  const [data, setData] = useState<DashboardData>()

  const getDashboardData = () => {
    const restUrl = `${Env.basePath}/api/dashboard`
    fetch(restUrl,
      {
        method: 'GET',
        credentials: 'include',
        headers: requestHeaders,
      })
      .then(res => res.json())
      .then(json => {
        setData(json)
      })
      .catch(err => console.log(err))
  }

  useEffect(() => {
    getDashboardData()
  }, [])

  const deleteMachineRequest = () => {
    const restUrl = `${Env.basePath}/api/delete/machine/${deleteMachine}`
    fetch(restUrl,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: requestHeaders,
      })
      .then(() => {
        getDashboardData()
        setShowDeleteRequest(false)
        setDeleteMachine(undefined)
      })
      .catch(err => console.log(err))
  }

  // Funktion zur Lizenzverlängerung
  const extendLicenseRequest = () => {
    if (!extendLicenseId || !newExpiryDate) return

    const restUrl = `${Env.basePath}/api/license/extend/${extendLicenseId}`
    fetch(restUrl,
      {
        method: 'PUT',
        credentials: 'include',
        headers: requestHeaders,
        body: JSON.stringify({
          expires: newExpiryDate.toISOString(),
        }),
      })
      .then(res => res.json())
      .then(json => {
        setData(json)
        setShowExtendLicenseDialog(false)
        setExtendLicenseId(undefined)
        setNewExpiryDate(null)
      })
      .catch(err => console.log(err))
  }

  const getMachineAction = (machineId: string) => {
    return (
      <Box>
        <EditIcon
          sx={{ color: theme.palette.primary.main, cursor: 'pointer', marginRight: '5px' }}
          onClick={() => {
            history.push(`/${appName}/Machine/${machineId}`)
          }}
        />
        <DeleteOutlineIcon
          sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
          onClick={() => {
            setShowDeleteRequest(true)
            setDeleteMachine(machineId)
          }}
        />
      </Box>
    )
  }

  const getLicenseAction = (licenseId: string) => {
    return (
      <Box>
        <UpdateIcon
          sx={{ color: theme.palette.primary.main, cursor: 'pointer', marginRight: '5px' }}
          onClick={() => {
            // Setze ein Standarddatum (ein Jahr ab heute)
            const defaultDate = new Date()
            defaultDate.setFullYear(defaultDate.getFullYear() + 1)

            setShowExtendLicenseDialog(true)
            setExtendLicenseId(licenseId)
            setNewExpiryDate(defaultDate)
          }}
        />
        <EditIcon
          sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
          onClick={() => {
            history.push(`/${appName}/License/${licenseId}`)
          }}
        />
      </Box>
    )
  }

  return (
    <>
      <Grid container className={classes.root}>
        <Grid item xs={12} container spacing={3}>
          <Grid item xs={12}>
            <Typography variant='h4'>
              Willkommen in der Lizenzverwaltung
            </Typography>
          </Grid>
          <Grid item xs={12} container spacing={3}>
            <Grid item xs={downMd ? 12 : 4}>
              <CardComponent padding={0} height={downMd ? 'unset' : 280}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', padding: '8px 8px 0 8px' }}>
                    <ReportIcon sx={{ marginRight: '5px', height: '1.5em', width: '1.5em', color: theme.palette.error.main }} />
                    <Typography sx={{ padding: downMd ? 2 : 0, paddingBottom: 0 }} variant='h6'>
                      <b>
                        Maschinen ohne Lizenz
                      </b>
                    </Typography>
                  </Box>
                  <Divider />
                  <TableContainer>
                    <Table>
                      {!downMd &&
                        <TableHead>
                          <StyledTableHead>
                            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Maschinen-ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Aktionen</TableCell>
                          </StyledTableHead>
                        </TableHead>}
                      <TableBody>
                        {data && data.machinesWithoutLicense?.map((machine: MachineData, index: number) => (
                          <>
                            {machine.machineKey &&
                              <StyledTableRow
                                key={index}
                              >
                                {downMd ? (
                                  <TableCell>
                                    <Grid container spacing={1}>
                                      <Grid item xs={6}>Name</Grid>
                                      <Grid item xs={6}><Typography>{machine.machineName}</Typography></Grid>
                                      <Grid item xs={6}>Maschinen-ID</Grid>
                                      <Grid item xs={6}><Typography>{machine.machineKey}</Typography></Grid>
                                      <Grid item xs={6}>Aktionen</Grid>
                                      <Grid item xs={6}><Typography>{getMachineAction(machine.machineId)}</Typography></Grid>
                                    </Grid>
                                  </TableCell>)
                                  : (
                                    <>
                                      <TableCell>{machine.machineName}</TableCell>
                                      <TableCell>{machine.machineKey}</TableCell>
                                      <TableCell>{getMachineAction(machine.machineId)}</TableCell>
                                    </>)}
                              </StyledTableRow>}
                          </>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardComponent>
            </Grid>
            <Grid item xs={downMd ? 12 : 4}>
              <CardComponent padding={0} height={downMd ? 'unset' : 280}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', padding: '8px 8px 0 8px' }}>
                    <ReportIcon sx={{ marginRight: '5px', height: '1.5em', width: '1.5em', color: theme.palette.error.main }} />
                    <Typography sx={{ padding: downMd ? 2 : 0, paddingBottom: 0 }} variant='h6'>
                      <b>
                        Abgelaufene Lizenzen
                      </b>
                    </Typography>
                  </Box>
                  <Divider />
                  <TableContainer>
                    <Table>
                      {!downMd &&
                        <TableHead>
                          <StyledTableHead>
                            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>abgelaufen am</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Aktionen</TableCell>
                          </StyledTableHead>
                        </TableHead>}
                      <TableBody>
                        {data && data.expiredLicenses?.map((license: LicenseData, index: number) => (
                          <>
                            <StyledTableRow
                              key={index}
                            >
                              {downMd ? (
                                <TableCell>
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>Name</Grid>
                                    <Grid item xs={6}><Typography>{license.name}</Typography></Grid>
                                    <Grid item xs={6}>abgelaufen am</Grid>
                                    <Grid item xs={6}><Typography>{formatDate(new Date(license.expires).getTime() / 1000)}</Typography></Grid>
                                    <Grid item xs={6}>Aktionen</Grid>
                                    <Grid item xs={6}><Typography>{getLicenseAction(license.id)}</Typography></Grid>
                                  </Grid>
                                </TableCell>)
                                : (
                                  <>
                                    <TableCell>{license.name}</TableCell>
                                    <TableCell>{formatDate(new Date(license.expires).getTime() / 1000)}</TableCell>
                                    <TableCell>{getLicenseAction(license.id)}</TableCell>
                                  </>)}
                            </StyledTableRow>
                          </>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardComponent>
            </Grid>
            <Grid item xs={downMd ? 12 : 4}>
              <CardComponent padding={0} height={downMd ? 'unset' : 280}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', padding: '8px 8px 0 8px' }}>
                    <ReportProblemIcon sx={{ marginRight: '5px', height: '1.5em', width: '1.5em', color: theme.palette.warning.main }} />
                    <Typography sx={{ padding: downMd ? 2 : 0, paddingBottom: 0 }} variant='h6'>
                      <b>
                        Lizenz endet bald
                      </b>
                    </Typography>
                  </Box>
                  <Divider />
                  <TableContainer>
                    <Table>
                      {!downMd &&
                        <TableHead>
                          <StyledTableHead>
                            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>läuft ab am</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Aktionen</TableCell>
                          </StyledTableHead>
                        </TableHead>}
                      <TableBody>
                        {data && data.soonExpiredLicenses?.map((license: LicenseData, index: number) => (
                          <>
                            <StyledTableRow
                              key={index}
                            >
                              {downMd ? (
                                <TableCell>
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>Name</Grid>
                                    <Grid item xs={6}><Typography>{license.name}</Typography></Grid>
                                    <Grid item xs={6}>läuft ab am</Grid>
                                    <Grid item xs={6}><Typography>{formatDate(new Date(license.expires).getTime() / 1000)}</Typography></Grid>
                                    <Grid item xs={6}>Aktionen</Grid>
                                    <Grid item xs={6}><Typography>{getLicenseAction(license.id)}</Typography></Grid>
                                  </Grid>
                                </TableCell>)
                                : (
                                  <>
                                    <TableCell>{license.name}</TableCell>
                                    <TableCell>{formatDate(new Date(license.expires).getTime() / 1000)}</TableCell>
                                    <TableCell>{getLicenseAction(license.id)}</TableCell>
                                  </>)}
                            </StyledTableRow>
                          </>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardComponent>
            </Grid>
            <Grid item xs={12}>
              <CardComponent padding={0}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', padding: '8px 8px 0 8px' }}>
                    <ReportProblemIcon sx={{ marginRight: '5px', height: '1.5em', width: '1.5em', color: theme.palette.warning.main }} />
                    <Typography sx={{ padding: downMd ? 2 : 0, paddingBottom: 0 }} variant='h6'>
                      <b>
                        Neuesten 10 Error Logs
                      </b>
                    </Typography>
                  </Box>
                  <Divider />
                  <TableContainer>
                    <Table>
                      {!downMd &&
                        <TableHead>
                          <StyledTableHead>
                            <TableCell sx={{ fontWeight: 600 }}>Fehler</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Lizenzschlüssel</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Maschinenname</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Maschinen-ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>erstellt am</TableCell>
                          </StyledTableHead>
                        </TableHead>}
                      <TableBody>
                        {data && data.errorLogs?.map((error: ErrorLogData, index: number) => (
                          <>
                            <StyledTableRow
                              key={index}
                            >
                              {downMd ? (
                                <TableCell>
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>Fehler</Grid>
                                    <Grid item xs={6}><Typography>{t(`error:${error.error}`)}</Typography></Grid>
                                    <Grid item xs={6}>Lizenzschlüssel</Grid>
                                    <Grid item xs={6}><Typography>{error.licenseKey}</Typography></Grid>
                                    <Grid item xs={6}>Maschinenname</Grid>
                                    <Grid item xs={6}><Typography>{error.machineName}</Typography></Grid>
                                    <Grid item xs={6}>Maschinen-Id</Grid>
                                    <Grid item xs={6}><Typography>{error.machineKey}</Typography></Grid>
                                    <Grid item xs={6}>erstellt am</Grid>
                                    <Grid item xs={6}><Typography>{formatDate(new Date(error.createdAt).getTime() / 1000, Format.dateTime)}</Typography></Grid>
                                  </Grid>
                                </TableCell>)
                                : (
                                  <>
                                    <TableCell>{t(`error:${error.error}`)}</TableCell>
                                    <TableCell>{error.licenseKey}</TableCell>
                                    <TableCell>{error.machineName}</TableCell>
                                    <TableCell>{error.machineKey}</TableCell>
                                    <TableCell>{formatDate(new Date(error.createdAt).getTime() / 1000, Format.dateTime)}</TableCell>
                                  </>)}
                            </StyledTableRow>
                          </>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardComponent>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Dialog
        open={showDeleteRequest}
      >
        <DialogTitle>
          Maschine löschen
        </DialogTitle>
        <DialogContent>
          <Typography variant='body1'>Wollen Sie diese Maschine wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden!</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color='secondary'
            onClick={() => {
              setShowDeleteRequest(false)
              setDeleteMachine(undefined)
            }}
          >
            Abbrechen
          </Button>
          <Button
            color='primary'
            onClick={() => {
              deleteMachineRequest()
            }}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showExtendLicenseDialog}
      >
        <DialogTitle>
          Lizenz verlängern
        </DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 2 }}>
            Bitte wählen Sie das neue Ablaufdatum für die Lizenz:
          </Typography>
          <TextField
            label='Neues Ablaufdatum'
            type='date'
            value={newExpiryDate ? newExpiryDate.toISOString().split('T')[0] : ''}
            onChange={(e) => {
              if (e.target.value) {
                setNewExpiryDate(new Date(e.target.value))
              } else {
                setNewExpiryDate(null)
              }
            }}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            margin='normal'
          />

        </DialogContent>
        <DialogActions>
          <Button
            color='secondary'
            onClick={() => {
              setShowExtendLicenseDialog(false)
              setExtendLicenseId(undefined)
              setNewExpiryDate(null)
            }}
          >
            Abbrechen
          </Button>
          <Button
            color='primary'
            onClick={() => {
              extendLicenseRequest()
            }}
          >
            Verlängern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
export default Dashboard
